/**
 * Medications Slice
 *
 * State is normalized by patientId so multiple open patients don't collide.
 *
 * Structure:
 *   byPatientId[patientId] = {
 *     emrSupplements      — tracked supplements in EMR (checked/status/source)
 *     emrDispensed        — tracked dispensed items in EMR
 *     orderSupplements    — from treatment orders (reference only)
 *     activeDispensed     — from active dispensed orders (reference only)
 *     patientPlanMeds     — recommended from treatment plan
 *     historicalMeds      — historical from treatment plan
 *     customMedications   — manually added (supplementName + frequency)
 *   }
 */

import { createSlice } from '@reduxjs/toolkit';
import {
  loadAllMedicationSources,
  addCustomMedication,
  loadDefaultItems,
  syncOrdersWithEMR,
} from '../thunks/medicationsThunks';

const makeEmptyPatientMeds = () => ({
  emrSupplements: [],
  emrDispensed: [],
  orderSupplements: [],
  activeDispensed: [],
  patientPlanMeds: [],
  historicalMeds: [],
  customMedications: [],
});

const initialState = {
  byPatientId: {},
  loading: {},   // { [patientId]: { initial, adding, syncing } }
  error: {},     // { [patientId]: string | null }

  // Global catalog — loaded once, not per-patient
  defaults: {
    supplements: [],  // enriched with dosages + frequencyOptions
    dispensed: [],
    loaded: false,
    loading: false,
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const getOrInit = (state, patientId) => {
  if (!state.byPatientId[patientId]) {
    state.byPatientId[patientId] = makeEmptyPatientMeds();
  }
  if (!state.loading[patientId]) {
    state.loading[patientId] = { initial: false, adding: false, syncing: false };
  }
  return state.byPatientId[patientId];
};

const updateItemInList = (list, updatedItem, keyFn = (i) => i.name) => {
  const key = keyFn(updatedItem);
  const idx = list.findIndex((i) => keyFn(i) === key);
  if (idx !== -1) {
    list[idx] = updatedItem;
  } else {
    list.push(updatedItem);
  }
};

// ─── Slice ─────────────────────────────────────────────────────────────────────

const medicationsSlice = createSlice({
  name: 'medications',
  initialState,
  reducers: {
    /**
     * Immediately push an optimistic custom medication item.
     */
    optimisticAddMedication: (state, action) => {
      const { patientId, item } = action.payload;
      const data = getOrInit(state, patientId);
      data.customMedications.push(item);
    },

    /**
     * Remove the temp optimistic item on failure.
     */
    rollbackOptimisticMedication: (state, action) => {
      const { patientId, tempId } = action.payload;
      const data = getOrInit(state, patientId);
      data.customMedications = data.customMedications.filter((m) => m._id !== tempId);
    },

    /**
     * Immediately update an EMR supplement or dispensed item in local state.
     * type: 'supplement' | 'dispensed'
     */
    updateEMRItemLocally: (state, action) => {
      const { patientId, item, type } = action.payload;
      const data = getOrInit(state, patientId);
      const listKey = type === 'supplement' ? 'emrSupplements' : 'emrDispensed';
      updateItemInList(data[listKey], item);
    },

    /**
     * Clear all medication data for a patient (called when tab is closed).
     */
    clearPatientMedications: (state, action) => {
      const { patientId } = action.payload;
      delete state.byPatientId[patientId];
      delete state.loading[patientId];
      delete state.error[patientId];
    },
  },

  extraReducers: (builder) => {
    // ── loadAllMedicationSources ─────────────────────────────────────────────
    builder
      .addCase(loadAllMedicationSources.pending, (state, action) => {
        const { patientId } = action.meta.arg;
        getOrInit(state, patientId);
        state.loading[patientId].initial = true;
        state.error[patientId] = null;
      })
      .addCase(loadAllMedicationSources.fulfilled, (state, action) => {
        const {
          patientId,
          emrSupplements,
          emrDispensed,
          activeDispensed,
          orderSupplements,
          patientPlanMeds,
          historicalMeds,
          customMedications,
        } = action.payload;
        const data = getOrInit(state, patientId);
        data.emrSupplements = emrSupplements;
        data.emrDispensed = emrDispensed;
        data.activeDispensed = activeDispensed;
        data.orderSupplements = orderSupplements;
        data.patientPlanMeds = patientPlanMeds;
        data.historicalMeds = historicalMeds;
        data.customMedications = customMedications;
        state.loading[patientId].initial = false;
      })
      .addCase(loadAllMedicationSources.rejected, (state, action) => {
        const { patientId } = action.meta.arg;
        // Delete entry so isLoaded goes back to false — allows retry on next render
        delete state.byPatientId[patientId];
        if (!state.loading[patientId]) state.loading[patientId] = { initial: false, adding: false, syncing: false };
        state.loading[patientId].initial = false;
        state.error[patientId] = action.payload;
      });

    // ── addCustomMedication ──────────────────────────────────────────────────
    builder
      .addCase(addCustomMedication.pending, (state, action) => {
        const { patientId } = action.meta.arg;
        getOrInit(state, patientId);
        state.loading[patientId].adding = true;
      })
      .addCase(addCustomMedication.fulfilled, (state, action) => {
        const { patientId, medications, tempId } = action.payload;
        const data = getOrInit(state, patientId);
        // Replace temp item with full server response list
        data.customMedications = medications;
        state.loading[patientId].adding = false;
      })
      .addCase(addCustomMedication.rejected, (state, action) => {
        const { patientId } = action.meta.arg;
        getOrInit(state, patientId);
        state.loading[patientId].adding = false;
        // Rollback already handled by the thunk dispatching rollbackOptimisticMedication
      });

    // ── syncOrdersWithEMR ────────────────────────────────────────────────────
    builder
      .addCase(syncOrdersWithEMR.pending, (state, action) => {
        const { patientId } = action.meta.arg;
        getOrInit(state, patientId);
        state.loading[patientId].syncing = true;
      })
      .addCase(syncOrdersWithEMR.fulfilled, (state, action) => {
        const { patientId, emrSupplements, emrDispensed } = action.payload;
        const data = getOrInit(state, patientId);
        data.emrSupplements = emrSupplements;
        data.emrDispensed = emrDispensed;
        state.loading[patientId].syncing = false;
      })
      .addCase(syncOrdersWithEMR.rejected, (state, action) => {
        const { patientId } = action.meta.arg;
        getOrInit(state, patientId);
        state.loading[patientId].syncing = false;
      });

    // ── loadDefaultItems ─────────────────────────────────────────────────────
    builder
      .addCase(loadDefaultItems.pending, (state) => {
        state.defaults.loading = true;
      })
      .addCase(loadDefaultItems.fulfilled, (state, action) => {
        state.defaults.supplements = action.payload.supplements;
        state.defaults.dispensed = action.payload.dispensed;
        state.defaults.loaded = true;
        state.defaults.loading = false;
      })
      .addCase(loadDefaultItems.rejected, (state) => {
        state.defaults.loading = false;
      });
  },
});

export const {
  optimisticAddMedication,
  rollbackOptimisticMedication,
  updateEMRItemLocally,
  clearPatientMedications,
} = medicationsSlice.actions;

export default medicationsSlice.reducer;
